import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useAuth } from '../lib/auth';
import { AppShell } from '../layouts/AppShell';
import { Login } from '../routes/Login';
import { Home } from '../routes/Home';
import { Examination } from '../routes/Examination';
import { Records } from '../routes/Records';
import { Fitness } from '../routes/Fitness';
import { Notifications } from '../routes/Notifications';
import { ProfilePage } from '../routes/Profile';
import { Consent } from '../routes/Consent';

function RequireAuth({ children }: { children: ReactNode }) {
  const { employee, bootstrapping } = useAuth();
  const loc = useLocation();

  // A worker opens this app a few times a year. Bouncing them to a login
  // screen during the moment the session is being resumed would make the app
  // feel broken every single time.
  if (bootstrapping) {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-neutral-300 border-t-primary-600" />
      </div>
    );
  }

  if (!employee) return <Navigate to="/login" replace state={{ from: loc.pathname }} />;
  return <>{children}</>;
}

export function AppRouter() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />

      <Route element={<RequireAuth><AppShell /></RequireAuth>}>
        <Route path="/home" element={<Home />} />
        <Route path="/examination" element={<Examination />} />
        <Route path="/records" element={<Records />} />
        <Route path="/fitness" element={<Fitness />} />
        <Route path="/notifications" element={<Notifications />} />
        <Route path="/profile" element={<ProfilePage />} />
        {/* Consent is reached from Home or a notification, never the nav bar —
            it is a task, not a place. */}
        <Route path="/consent/:orderId" element={<Consent />} />
      </Route>

      <Route path="/" element={<Navigate to="/home" replace />} />
      <Route
        path="*"
        element={
          <div className="flex min-h-dvh items-center justify-center px-6 text-center text-sm text-neutral-600">
            That page does not exist.
          </div>
        }
      />
    </Routes>
  );
}
